import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useWorkspace } from '../hooks/useWorkspace';
import { useWorkspaceLimit } from '../hooks/useWorkspaceLimit';
import { api } from '../lib/api';
import { WorkspacePaywall } from './upgrade';
import { trackWorkspaceCreated, trackStripeConnected } from '../lib/analytics';

interface CreateWorkspaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Pass an existing workspace ID to skip profile creation and go straight to Stripe */
  existingWorkspaceId?: string | null;
}

type Step = 'profile' | 'stripe' | 'progress';
type SyncProgress = 'connecting' | 'syncing' | 'done' | 'error';

interface StripeStatus {
  connected: boolean;
  syncStatus?: string;
  syncError?: string | null;
}

export default function CreateWorkspaceModal({
  isOpen,
  onClose,
  onSuccess,
  existingWorkspaceId,
}: CreateWorkspaceModalProps) {
  const { createWorkspace, refreshWorkspaces } = useWorkspace();
  const { canCreateWorkspace, plan, currentCount, limit } = useWorkspaceLimit();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const [step, setStep] = useState<Step>('profile');

  // Profile state
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [tagline, setTagline] = useState('');

  // Stripe state
  const [stripeApiKey, setStripeApiKey] = useState('');

  // Form state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Created workspace ID (persists across steps)
  const workspaceIdRef = useRef<string | null>(null);

  // Sync progress state
  const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null);
  const [syncStatus, setSyncStatus] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  // Focus input when modal opens or step changes
  useEffect(() => {
    if (isOpen && step === 'profile' && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, step]);

  // Reset form when modal closes, or init for existing workspace
  useEffect(() => {
    if (!isOpen) {
      setName('');
      setCompanyName('');
      setIndustry('');
      setTagline('');
      setStripeApiKey('');
      setError(null);
      setStep('profile');
      setSyncProgress(null);
      setSyncStatus(null);
      setSyncError(null);
      workspaceIdRef.current = null;
    } else if (existingWorkspaceId) {
      // Skip profile step for existing workspaces
      workspaceIdRef.current = existingWorkspaceId;
      setStep('stripe');
    }
  }, [isOpen, existingWorkspaceId]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const pollSyncStatus = useCallback((workspaceId: string) => {
    if (pollRef.current) clearInterval(pollRef.current);

    pollRef.current = setInterval(async () => {
      try {
        const status = await api.get<StripeStatus>(`/stripe/${workspaceId}/status`);
        if (status.syncStatus) {
          setSyncStatus(status.syncStatus);
        }
        if (status.syncStatus === 'idle') {
          setSyncProgress('done');
          trackStripeConnected();
          if (pollRef.current) clearInterval(pollRef.current);
          // Auto-close and redirect to dashboard after a short delay so user sees "done"
          setTimeout(async () => {
            await refreshWorkspaces();
            queryClient.invalidateQueries({ queryKey: ['dashboard', workspaceId] });
            onSuccess?.();
            onClose();
            navigate('/dashboard');
          }, 1500);
        } else if (status.syncStatus === 'error') {
          setSyncProgress('error');
          setSyncError(status.syncError || 'Sync failed');
          if (pollRef.current) clearInterval(pollRef.current);
        }
      } catch {
        // Ignore polling errors, will retry
      }
    }, 5000);
  }, [onClose, onSuccess, queryClient, refreshWorkspaces, navigate]);

  // Step 1: Create workspace from profile, then move to Stripe tab
  const handleCreateWorkspace = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Workspace name is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const workspace = await createWorkspace(name.trim());
      trackWorkspaceCreated();
      workspaceIdRef.current = workspace.workspaceId;

      if (companyName.trim() || industry.trim() || tagline.trim()) {
        await api.patch(`/workspaces/${workspace.workspaceId}`, {
          companyName: companyName.trim() || undefined,
          industry: industry.trim() || undefined,
          tagline: tagline.trim() || undefined,
        });
      }

      setStep('stripe');
    } catch (err: unknown) {
      const apiError = err as { message?: string; status?: number; code?: string };
      if (apiError.status === 409 && apiError.code === 'WORKSPACE_LIMIT_REACHED') {
        setError(apiError.message || 'Workspace limit reached. Please upgrade your plan.');
      } else if (apiError.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError(apiError.message || 'Failed to create workspace. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 2: Connect Stripe, then move to progress view
  const handleConnectStripe = async (e: React.FormEvent) => {
    e.preventDefault();

    const workspaceId = workspaceIdRef.current;
    if (!workspaceId) return;

    if (!stripeApiKey.trim()) {
      setError('Stripe API key is required');
      return;
    }

    if (!stripeApiKey.startsWith('sk_') && !stripeApiKey.startsWith('rk_')) {
      setError('Invalid API key format. Please use a Stripe Secret Key (starts with sk_ or rk_)');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      setStep('progress');
      setSyncProgress('connecting');

      await api.post(`/stripe/${workspaceId}/connect`, {
        apiKey: stripeApiKey,
      });

      // Stripe connected — backfill starts automatically
      setSyncProgress('syncing');
      setSyncStatus('pending');
      pollSyncStatus(workspaceId);
    } catch (err: unknown) {
      const apiError = err as { message?: string; status?: number };
      setSyncProgress('error');
      setSyncError(apiError.message || 'Failed to connect Stripe');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    // If a workspace was created during this session, refresh so dashboard picks it up
    if (workspaceIdRef.current) {
      refreshWorkspaces();
    }
    onClose();
  };

  if (!isOpen) return null;

  const isProfileValid = name.trim().length > 0;
  const isStripeValid = stripeApiKey.trim().length > 0 && (stripeApiKey.startsWith('sk_') || stripeApiKey.startsWith('rk_'));

  // ─── Progress View ───
  if (step === 'progress') {
    return (
      <div className="fixed inset-x-0 top-16 bottom-0 z-50 overflow-y-auto">
        <div className="fixed inset-x-0 top-16 bottom-0 bg-gray-50 transition-opacity" onClick={handleClose} />
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg transform transition-all">
            <div className="px-6 pt-6 pb-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  Setting up your workspace
                </h2>
                <button onClick={handleClose} className="text-gray-400 hover:text-gray-500 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="px-6 py-8">
              {syncProgress !== 'error' && (
                <div className="text-center mb-6">
                  {syncProgress === 'done' ? (
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                      <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  ) : (
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto" />
                  )}
                </div>
              )}

              <SyncSteps syncProgress={syncProgress} syncStatus={syncStatus} />

              {syncProgress === 'error' && syncError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{syncError}</p>
                </div>
              )}

              <p className="text-sm text-gray-500 text-center mt-6">
                {syncProgress === 'done'
                  ? 'Your dashboard is ready! Loading...'
                  : syncProgress === 'error'
                    ? 'You can retry from workspace settings.'
                    : 'This usually takes 2-5 minutes depending on your account size.'}
              </p>
            </div>

            <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex items-center justify-end space-x-3">
              {syncProgress === 'error' ? (
                <button onClick={handleClose} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors">
                  Close
                </button>
              ) : syncProgress !== 'done' ? (
                <button onClick={handleClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                  Continue in Background
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Defense-in-depth: block creation if at workspace limit ───
  if (!canCreateWorkspace && !existingWorkspaceId && !workspaceIdRef.current) {
    return (
      <div className="fixed inset-x-0 top-16 bottom-0 z-50 overflow-y-auto">
        <div className="fixed inset-x-0 top-16 bottom-0 bg-gray-50 transition-opacity" onClick={onClose} />
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm transform transition-all p-6">
            <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-500">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <WorkspacePaywall plan={plan} currentCount={currentCount} limit={limit} />
          </div>
        </div>
      </div>
    );
  }

  // ─── Profile & Stripe Tabs ───
  const tabs: { id: 'profile' | 'stripe'; label: string }[] = [
    { id: 'profile', label: 'Profile' },
    { id: 'stripe', label: 'Stripe' },
  ];

  // Stripe tab is only clickable after workspace is created
  const isWorkspaceCreated = !!workspaceIdRef.current;

  return (
    <div className="fixed inset-x-0 top-16 bottom-0 z-50 overflow-y-auto">
      <div className="fixed inset-x-0 top-16 bottom-0 bg-gray-50 transition-opacity" onClick={onClose} />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl transform transition-all">
          {/* Header */}
          <div className="px-6 pt-6 pb-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {existingWorkspaceId ? 'Connect Stripe' : 'Create new workspace'}
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  {existingWorkspaceId
                    ? 'Connect your Stripe account to see your SaaS metrics.'
                    : 'Set up your workspace and connect your Stripe account.'}
                </p>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Tabs */}
            <nav className="flex space-x-6 mt-4 -mb-px">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => {
                    if (tab.id === 'stripe' && !isWorkspaceCreated) return;
                    setStep(tab.id);
                  }}
                  disabled={tab.id === 'stripe' && !isWorkspaceCreated}
                  className={`pb-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                    step === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : tab.id === 'stripe' && !isWorkspaceCreated
                        ? 'border-transparent text-gray-300 cursor-not-allowed'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                  {tab.id === 'profile' && isWorkspaceCreated && (
                    <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                  {tab.id === 'stripe' && isStripeValid && (
                    <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="px-6 py-6 max-h-[60vh] overflow-y-auto">
            {/* Profile Step */}
            {step === 'profile' && (
              <form id="profile-form" onSubmit={handleCreateWorkspace}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Workspace Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      ref={inputRef}
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      placeholder="My SaaS Product"
                      maxLength={50}
                      disabled={isSubmitting || isWorkspaceCreated}
                    />
                    <p className="mt-1 text-xs text-gray-500">{name.length}/50 characters</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Name <span className="text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      placeholder="Acme Inc."
                      disabled={isSubmitting || isWorkspaceCreated}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Industry <span className="text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={industry}
                      onChange={(e) => setIndustry(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      placeholder="SaaS, E-commerce, Fintech..."
                      disabled={isSubmitting || isWorkspaceCreated}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tagline <span className="text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={tagline}
                      onChange={(e) => setTagline(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      placeholder="A short description of your product"
                      maxLength={100}
                      disabled={isSubmitting || isWorkspaceCreated}
                    />
                  </div>
                </div>
              </form>
            )}

            {/* Stripe Step */}
            {step === 'stripe' && (
              <form id="stripe-form" onSubmit={handleConnectStripe}>
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Connect your Stripe account to see your SaaS metrics.
                  </p>

                  <div className="flex gap-4 text-xs text-gray-600">
                    <div className="flex items-center gap-1.5">
                      <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Read-only access
                    </div>
                    <div className="flex items-center gap-1.5">
                      <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Encrypted storage
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Stripe Secret Key <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      value={stripeApiKey}
                      onChange={(e) => setStripeApiKey(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors font-mono text-sm"
                      placeholder="sk_live_... or sk_test_..."
                      disabled={isSubmitting}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Find your API key in{' '}
                      <a
                        href="https://dashboard.stripe.com/apikeys"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700"
                      >
                        Stripe Dashboard → Developers → API Keys
                      </a>
                    </p>
                  </div>

                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-700">
                      Your API key is encrypted and stored securely. We only read subscription data.
                    </p>
                  </div>
                </div>
              </form>
            )}

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex items-center justify-between">
            <div className="text-xs text-gray-500">
              {step === 'profile' && !isProfileValid && 'Workspace name required'}
              {step === 'profile' && isProfileValid && 'Click Next to continue'}
              {step === 'stripe' && !isStripeValid && 'Stripe key required'}
              {step === 'stripe' && isStripeValid && 'Ready to connect'}
            </div>
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>

              {step === 'profile' && (
                <button
                  type="submit"
                  form="profile-form"
                  disabled={isSubmitting || !isProfileValid}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Creating...</span>
                    </>
                  ) : (
                    <span>Next: Connect Stripe →</span>
                  )}
                </button>
              )}

              {step === 'stripe' && (
                <button
                  type="submit"
                  form="stripe-form"
                  disabled={isSubmitting || !isStripeValid}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Connecting...</span>
                    </>
                  ) : (
                    <span>Connect Stripe</span>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SyncStepIcon({ status }: { status: 'done' | 'active' | 'pending' }) {
  if (status === 'done') {
    return (
      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
        <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
    );
  }
  if (status === 'active') {
    return (
      <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center">
        <div className="w-3 h-3 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return (
    <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
      <div className="w-2 h-2 bg-gray-300 rounded-full" />
    </div>
  );
}

function SyncSteps({ syncProgress, syncStatus }: { syncProgress: SyncProgress | null; syncStatus: string | null }) {
  const stripeConnected = syncProgress !== 'connecting';
  const importDone = syncStatus === 'idle' || syncStatus === 'error';
  const metricsDone = importDone && syncProgress === 'done';

  const steps = [
    { label: 'Workspace created', status: 'done' as const },
    {
      label: stripeConnected ? 'Stripe connected' : 'Connecting Stripe...',
      status: stripeConnected ? 'done' as const : 'active' as const,
    },
    {
      label: importDone ? 'Data imported' : 'Importing your data...',
      status: (stripeConnected ? (importDone ? 'done' : 'active') : 'pending') as 'done' | 'active' | 'pending',
    },
    {
      label: metricsDone ? 'Metrics calculated' : 'Calculating metrics',
      status: (importDone ? (metricsDone ? 'done' : 'active') : 'pending') as 'done' | 'active' | 'pending',
    },
    {
      label: 'Dashboard ready!',
      status: (metricsDone ? 'done' : 'pending') as 'done' | 'active' | 'pending',
    },
  ];

  return (
    <div className="space-y-3">
      {steps.map((step) => (
        <div key={step.label} className="flex items-center gap-3">
          <SyncStepIcon status={step.status} />
          <span className={`text-sm ${
            step.status === 'done' ? 'text-gray-600' :
            step.status === 'active' ? 'text-gray-900 font-medium' :
            'text-gray-400'
          }`}>
            {step.label}
          </span>
        </div>
      ))}
    </div>
  );
}
